export const median = (values) => {
  if (values.length === 0) return 0;

  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);

  return sorted.length % 2 === 0
    ? (sorted[middle - 1] + sorted[middle]) / 2
    : sorted[middle];
};

const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);

const buildExpectedMissingHeights = (startHeight, deltaHeight, typicalHeightStep) => {
  if (typicalHeightStep <= 0 || deltaHeight <= typicalHeightStep * 1.5) {
    return [];
  }

  const estimatedStepCount = Math.round(deltaHeight / typicalHeightStep);
  const reconstructedDelta = estimatedStepCount * typicalHeightStep;

  if (estimatedStepCount < 2 || Math.abs(deltaHeight - reconstructedDelta) > typicalHeightStep * 0.35) {
    return [];
  }

  return Array.from({ length: estimatedStepCount - 1 }, (_, index) => (
    Number((startHeight + (typicalHeightStep * (index + 1))).toFixed(3))
  ));
};

export const buildBoundaryContext = (previousTable, currentTable) => {
  if (
    !previousTable
    || !currentTable
    || !Array.isArray(previousTable.heights)
    || !Array.isArray(previousTable.volumes)
    || !Array.isArray(currentTable.heights)
    || !Array.isArray(currentTable.volumes)
    || previousTable.heights.length === 0
    || currentTable.heights.length === 0
  ) {
    return null;
  }

  const previousIndex = previousTable.heights.length - 1;

  return {
    previousHeight: previousTable.heights[previousIndex],
    previousVolume: previousTable.volumes[previousIndex],
    previousPageNumber: previousTable.pageNumber,
    currentHeight: currentTable.heights[0],
    currentVolume: currentTable.volumes[0],
    currentPageNumber: currentTable.pageNumber,
  };
};

export const detectHeightVolumeGaps = (
  heights,
  volumes,
  pointPageNumbers = [],
  boundaryContext = null,
) => {
  const heightSteps = [];
  const volumeSteps = [];

  for (let index = 0; index < heights.length - 1; index += 1) {
    const heightStep = heights[index + 1] - heights[index];
    const volumeStep = volumes[index + 1] - volumes[index];

    if (heightStep > 0) heightSteps.push(heightStep);
    if (volumeStep > 0) volumeSteps.push(volumeStep);
  }

  const typicalHeightStep = median(heightSteps);
  const typicalVolumeStep = median(volumeSteps);
  const gaps = [];
  const anomalySegments = new Set();
  const anomalyPoints = new Set();

  const addGap = ({
    kind,
    segmentIndex = null,
    startHeight,
    startVolume,
    endHeight,
    endVolume,
    pageFrom,
    pageTo,
  }) => {
    if (!isFiniteNumber(startHeight) || !isFiniteNumber(endHeight)) return;

    const deltaHeight = endHeight - startHeight;
    const deltaVolume = isFiniteNumber(startVolume) && isFiniteNumber(endVolume)
      ? endVolume - startVolume
      : null;
    const hasHeightGap = typicalHeightStep > 0 && deltaHeight > typicalHeightStep * 1.5;
    const hasVolumeJump = typicalVolumeStep > 0
      && deltaVolume !== null
      && deltaVolume > typicalVolumeStep * 1.5;

    if (!hasHeightGap && !hasVolumeJump) return;

    if (typeof segmentIndex === 'number') {
      anomalySegments.add(segmentIndex);
      anomalyPoints.add(segmentIndex);
      anomalyPoints.add(segmentIndex + 1);
    } else {
      anomalyPoints.add(0);
    }

    gaps.push({
      id: `${kind}-${segmentIndex ?? 'boundary'}-${startHeight}-${endHeight}`,
      kind,
      segmentIndex,
      startHeight,
      startVolume,
      endHeight,
      endVolume,
      deltaHeight,
      deltaVolume,
      pageFrom,
      pageTo,
      hasHeightGap,
      hasVolumeJump,
      typicalHeightStep,
      typicalVolumeStep,
      expectedMissingHeights: buildExpectedMissingHeights(
        startHeight,
        deltaHeight,
        typicalHeightStep,
      ),
      label: kind === 'internal_gap' ? 'écart inhabituel' : 'écart entre pages ?',
    });
  };

  if (
    boundaryContext
    && isFiniteNumber(boundaryContext.previousHeight)
    && isFiniteNumber(boundaryContext.currentHeight)
    && heights.length > 0
  ) {
    addGap({
      kind: 'previous_boundary',
      startHeight: boundaryContext.previousHeight,
      startVolume: boundaryContext.previousVolume,
      endHeight: boundaryContext.currentHeight,
      endVolume: boundaryContext.currentVolume,
      pageFrom: boundaryContext.previousPageNumber,
      pageTo: boundaryContext.currentPageNumber,
    });
  }

  for (let index = 0; index < heights.length - 1; index += 1) {
    const isPageBoundary = pointPageNumbers[index] !== undefined
      && pointPageNumbers[index + 1] !== undefined
      && pointPageNumbers[index] !== pointPageNumbers[index + 1];

    addGap({
      kind: isPageBoundary ? 'page_boundary' : 'internal_gap',
      segmentIndex: index,
      startHeight: heights[index],
      startVolume: volumes[index],
      endHeight: heights[index + 1],
      endVolume: volumes[index + 1],
      pageFrom: pointPageNumbers[index],
      pageTo: pointPageNumbers[index + 1],
    });
  }

  return {
    gaps,
    heightGapSegments: gaps.filter((gap) => gap.hasHeightGap),
    boundaryGaps: gaps.filter((gap) => gap.kind === 'previous_boundary'),
    anomalySegments,
    anomalyPoints,
    anomalyCount: gaps.length,
    typicalHeightStep,
    typicalVolumeStep,
  };
};
